import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchAppConfig } from '../api/app-config';
import { brand as fallbackBrand } from '../lib/brand';

type BrandingState = {
  displayName: string;
  productName: string;
  tagline: string;
  ready: boolean;
};

const BrandingContext = createContext<{
  displayName: string;
  productName: string;
  tagline: string;
  ready: boolean;
  refresh: () => Promise<void>;
} | null>(null);

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BrandingState>({
    displayName: fallbackBrand.name,
    productName: fallbackBrand.name,
    tagline: fallbackBrand.tagline,
    ready: false,
  });

  const load = useCallback(async () => {
    const { data, error } = await fetchAppConfig();
    if (error || !data) {
      setState((s) => ({ ...s, ready: true }));
      return;
    }
    setState({
      displayName: data.displayName?.trim() || fallbackBrand.name,
      productName: data.product.name?.trim() || fallbackBrand.name,
      tagline: data.product.tagline || fallbackBrand.tagline,
      ready: true,
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.title = `${state.productName} · Employee`;
  }, [state.productName]);

  const value = useMemo(
    () => ({
      displayName: state.displayName,
      productName: state.productName,
      tagline: state.tagline,
      ready: state.ready,
      refresh: load,
    }),
    [state.displayName, state.productName, state.tagline, state.ready, load]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
